import { useMemo, useRef, useState } from "react";
import {
  Alert,
  Card,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, GridValueFormatterParams } from "@mui/x-data-grid";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import moment from "moment";
import { toast } from "react-toastify";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import financeService from "services/financeService";
import {
  BnsfRoadDisputeRow,
  BnsfRoadDisputeRowError,
  ImportBnsfRoadDisputesResult,
  PreviewBnsfRoadDisputesResult,
} from "finance/bnsfRoadDisputeModels";

/**
 * Money at 2 decimals.
 *
 * The staging table holds decimal(18,6) and the workbook's expected amounts genuinely carry up to
 * 5 decimals (472.75878 = 458.9 x 1.03), so this is a rounding of the stored figure. The totals
 * panel shows the SERVER's sums rather than a sum of these rounded cells, which means adding the
 * grid up by hand can land a cent or two away from the total — that is the display rounding, not
 * a parse error.
 */
const formatMoney = (value?: number): string =>
  value == null ? "" : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Miles and idle minutes: right-aligned like money, but without forcing cents onto a whole number. */
const formatQuantity = (value?: number): string =>
  value == null ? "" : value.toLocaleString(undefined, { maximumFractionDigits: 2 });

/** Per-unit rates only read correctly at full precision (1.03, 0.3975), so they keep 4 decimals. */
const formatRate = (value?: number): string =>
  value == null ? "" : value.toLocaleString(undefined, { maximumFractionDigits: 4 });

/**
 * Workbook wall-clock values. The cells carry no timezone, so they are shown exactly as parsed
 * with no conversion — moment() on an offset-less ISO string parses as local, which is what the
 * biller expects when checking a row against the spreadsheet.
 */
const formatDateTime = (value?: string): string => (value ? moment(value).format("M/D/YY HH:mm") : "");

/**
 * The workbook stores trip time as a fraction of a day (0.4462 = ~10.7 hours), which nobody can
 * read. It is shown in hours here and the header says so; the value that lands in the table is
 * still the fraction, so this column is a convenience, not the stored figure.
 */
const formatTripHours = (days?: number): string =>
  days == null ? "" : (days * 24).toLocaleString(undefined, { maximumFractionDigits: 2 });

const moneyColumn = (field: string, headerName: string, width = 115): GridColDef => ({
  field,
  headerName,
  width,
  // type "number" is what right-aligns both the header and the cell in the data grid.
  type: "number",
  valueFormatter: (params: GridValueFormatterParams<number>) => formatMoney(params.value),
});

const quantityColumn = (field: string, headerName: string, width = 105): GridColDef => ({
  field,
  headerName,
  width,
  type: "number",
  valueFormatter: (params: GridValueFormatterParams<number>) => formatQuantity(params.value),
});

const rateColumn = (field: string, headerName: string, width = 115): GridColDef => ({
  field,
  headerName,
  width,
  type: "number",
  valueFormatter: (params: GridValueFormatterParams<number>) => formatRate(params.value),
});

const dateColumn = (field: string, headerName: string): GridColDef => ({
  field,
  headerName,
  width: 125,
  valueFormatter: (params: GridValueFormatterParams<string>) => formatDateTime(params.value),
});

// Column order follows the workbook's own A-Z order so a row can be read side by side with the
// spreadsheet it came from. Headers are shortened from BNSF's (several of which contain line
// breaks) but keep BNSF's own "BN" / "RCX" prefixes, because those name whose number it is and
// the whole dispute is about the two disagreeing.
const disputeColumns: GridColDef[] = [
  {
    field: "sourceRowNumber",
    headerName: "Row",
    width: 70,
    type: "number",
    description: "Row number in the uploaded spreadsheet (the header is row 1).",
  },
  { field: "tripId", headerName: "Trip Number", width: 110, type: "number" },
  { field: "controlNumber", headerName: "Control Number", width: 290 },
  { field: "tripType", headerName: "Trip Type", width: 100 },
  dateColumn("pickupDate", "Pickup Date"),
  dateColumn("beginTrip", "Begin Trip"),
  dateColumn("endTrip", "End Trip"),
  {
    field: "totalTripTimeDays",
    headerName: "Trip Time (h)",
    width: 110,
    type: "number",
    description: "Converted for reading. The workbook stores this as a fraction of a day, and that is what is stored.",
    valueFormatter: (params: GridValueFormatterParams<number>) => formatTripHours(params.value),
  },
  { field: "location", headerName: "Location", width: 150 },

  quantityColumn("bnsfMilesDriven", "BN Miles"),
  quantityColumn("rcxMiles", "RCX Miles"),
  moneyColumn("mileageAmountPaid", "Mileage Paid"),
  moneyColumn("mileageAmountExpected", "Mileage Expected", 140),

  quantityColumn("bnsfFuelMileage", "BN Fuel Miles"),
  quantityColumn("rcxFuelMiles", "RCX Fuel Miles", 120),
  rateColumn("fuelRate", "Fuel Rate", 100),
  moneyColumn("fuelAmountPaid", "Fuel Paid"),
  moneyColumn("fuelAmountExpected", "Fuel Expected", 125),

  quantityColumn("bnsfIdleMinutes", "BN Idle Min"),
  quantityColumn("rcxIdleMinutes", "RCX Idle Min", 115),
  moneyColumn("idleAmountPaid", "Idle Paid"),
  moneyColumn("idleAmountExpected", "Idle Expected", 125),

  rateColumn("contractMileageRate", "Contract Mileage Rate", 170),
  rateColumn("contractIdleRate", "Contract Idle Rate", 150),
  {
    field: "contractMinimumRate",
    headerName: "Contract Minimum Rate",
    width: 180,
    type: "number",
    description: "0 where the location has no contract minimum. Non-zero values are shown in bold.",
    // Most rows are legitimately 0 — the workbook omits the cell entirely when a location has no
    // contract minimum, and the parser collapses that to 0 (COLUMN-SPEC.md's blank-collapse rule).
    // The handful that DO carry one are exactly what Heidi checks on this screen, so they are
    // weighted up rather than left to be picked out of a column of identical zeroes.
    renderCell: (params: GridRenderCellParams<BnsfRoadDisputeRow, number>) => (
      <Typography
        variant="caption"
        fontWeight={params.value ? "bold" : "regular"}
        color={params.value ? "text.primary" : "text.secondary"}
      >
        {formatMoney(params.value)}
      </Typography>
    ),
  },

  moneyColumn("rcxTotalPaymentExpected", "RCX Total Expected", 155),
  moneyColumn("bnsfTotalPayment", "BNSF Total Paid", 140),
];

/**
 * idle  — nothing chosen, or a fresh file chosen and not previewed yet
 * previewing / importing — a request is in flight
 * previewed — a preview came back and Import is available
 * imported  — THIS file has been written; Import stays disabled until a new file is chosen
 */
type Phase = "idle" | "previewing" | "previewed" | "importing" | "imported";

/** One line of the totals block: label, value, and whether it is a headline figure. */
interface TotalLine {
  label: string;
  value: number;
  emphasis?: boolean;
}

const ErrorRowList = ({ errors }: { errors: BnsfRoadDisputeRowError[] }) => (
  // Scrolls rather than growing without bound: a badly generated workbook can fail every row, and
  // a 600-entry list would push the import button off the screen.
  <MDBox sx={{ maxHeight: 240, overflowY: "auto", mt: 1 }}>
    {errors.map((rowError) => (
      <Typography key={rowError.sourceRowNumber} variant="body2" display="block">
        <strong>Row {rowError.sourceRowNumber}</strong> — {rowError.message}
      </Typography>
    ))}
  </MDBox>
);

/**
 * Finance &gt; BNSF Road Disputes.
 *
 * Replaces hand-written INSERT statements for Heidi's monthly BNSF "Road Disputes" workbook —
 * the list of trips BNSF underpaid or did not pay, which RCX disputes. BNSF calls these trips
 * "Road". Upload the .xlsx, check what was parsed, then commit it to the dispute staging table.
 *
 * Two deliberate properties of this screen:
 *
 * 1. Nothing is read until the user presses Preview, and nothing is written until they then press
 *    Import — choosing a file does neither, and the preview endpoint writes nothing at all even
 *    once pressed. The import endpoint re-parses the SAME uploaded file
 *    server-side rather than accepting the rows shown below — these are disputed dollar amounts
 *    and the browser is not allowed to supply the numbers that land in the table — which is why
 *    the file is posted twice instead of the preview's rows being sent back.
 *
 * 2. The result of the write is stated in full, loudly, and the inserted count is checked against
 *    what the preview said was valid. Disputes age out and BNSF stops paying once they do, so a
 *    silent partial import is the expensive failure here: a batch that went in short looks exactly
 *    like a batch that went in whole unless the screen says otherwise. There is no idempotency key
 *    behind this endpoint either, so Import is locked while a request is in flight AND after a
 *    successful write of that file — a double-click cannot create a second batch.
 */
/**
 * `embedded` renders the screen as one card in a host page's grid (Finance > Ad Hoc Processes)
 * rather than as a page of its own: the page padding and the h5 belong to the host, so the
 * card carries the h6 heading instead. Ad Hoc Processes is its only host; there is no
 * standalone route.
 */
const BnsfRoadDisputes = ({ embedded = false }: { embedded?: boolean }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Synchronous re-entrancy guard. The disabled prop depends on a re-render having happened, so it
  // alone does not stop two clicks landing in the same tick — and the cost of that is a duplicate
  // dispute batch with no key to de-duplicate it by.
  const importInFlightRef = useRef(false);

  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [preview, setPreview] = useState<PreviewBnsfRoadDisputesResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportBnsfRoadDisputesResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const isBusy = phase === "previewing" || phase === "importing";

  const handleFileSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    // A new file resets everything, which is also what lifts the post-import Import lock.
    setFile(files[0]);
    setPhase("idle");
    setPreview(null);
    setPreviewError(null);
    setImportResult(null);
    setImportError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handlePreview = async () => {
    if (!file) return;
    setPhase("previewing");
    setPreview(null);
    setPreviewError(null);
    setImportResult(null);
    setImportError(null);
    try {
      const result = await financeService.previewBnsfRoadDisputes(file);
      setPreview(result);
      setPhase("previewed");
    } catch (e: any) {
      setPreviewError(e?.message ?? "Could not read the workbook. Check the file and try again.");
      setPhase("idle");
      toast.error("BNSF Road Disputes: could not read the workbook.");
    }
  };

  const handleImport = async () => {
    if (!file || phase !== "previewed" || importInFlightRef.current) return;
    importInFlightRef.current = true;
    setPhase("importing");
    setImportError(null);
    try {
      const result = await financeService.importBnsfRoadDisputes(file);
      setImportResult(result);
      setPhase("imported");
      toast.success(
        `BNSF Road Disputes: ${result.insertedCount} row${result.insertedCount === 1 ? "" : "s"} imported.`
      );
    } catch (e: any) {
      setImportError(e?.message ?? "Import failed and the result is unknown.");
      // Back to "previewed" so Import can be pressed again — but only after the operator has read
      // the message, because a failure after the proc committed is indistinguishable from one
      // before it, and retrying blind would double the batch.
      setPhase("previewed");
      toast.error("BNSF Road Disputes: import failed.");
    } finally {
      importInFlightRef.current = false;
    }
  };

  const handleCopyBatchId = async () => {
    if (!importResult?.uploadBatchId) return;
    try {
      await navigator.clipboard.writeText(importResult.uploadBatchId);
      toast.success("Upload batch ID copied.");
    } catch (e) {
      toast.error("Could not copy to clipboard.");
    }
  };

  const totalLines: TotalLine[] = useMemo(() => {
    if (!preview) return [];
    const totals = preview.totals;
    return [
      { label: "Mileage expected", value: totals.mileageAmountExpected },
      { label: "Fuel expected", value: totals.fuelAmountExpected },
      { label: "Idle expected", value: totals.idleAmountExpected },
      { label: "Contract minimums", value: totals.contractMinimumRate },
      { label: "RCX total expected", value: totals.rcxTotalPaymentExpected, emphasis: true },
      { label: "BNSF total paid", value: totals.bnsfTotalPayment },
      {
        label: "Disputed (expected less paid)",
        // Derived here rather than asked of the BFF: it is exactly the two figures above it, and
        // it is the number the dispute is for, so it belongs on screen.
        value: totals.rcxTotalPaymentExpected - totals.bnsfTotalPayment,
        emphasis: true,
      },
    ];
  }, [preview]);

  // The partial-import check. Preview and import re-parse the same file with the same parser, so
  // the preview's valid-row count is what the insert SHOULD return; anything else is reported
  // rather than rounded off into a success message.
  const insertShortfall = importResult && preview ? preview.validRowCount - importResult.insertedCount : 0;

  const canPreview = !!file && !isBusy && phase !== "imported";
  const canImport = phase === "previewed" && !!preview && preview.validRowCount > 0;

  return (
    <MDBox p={embedded ? 0 : 3}>
      {!embedded && (
        <Typography variant="h5" gutterBottom>
          BNSF Road Disputes
        </Typography>
      )}

      <Stack spacing={3}>
        <Card sx={{ p: 3 }}>
          {embedded && (
            <Typography variant="h6" gutterBottom>
              BNSF Road Disputes
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" mb={2}>
            Upload BNSF&apos;s monthly Road Disputes workbook (.xlsx) — the trips BNSF underpaid or did not pay. The
            file is read and checked first; nothing is saved until you press Import. Confirm the imported row count
            before you leave this page: a dispute that never made it into the batch ages out, and BNSF will not pay it
            afterwards.
          </Typography>

          <Stack spacing={2} sx={{ maxWidth: 520 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              style={{ display: "none" }}
              onChange={(e) => handleFileSelected(e.target.files)}
            />
            <MDBox display="flex" alignItems="center" gap={2}>
              <MDButton
                variant="contained"
                color="info"
                disabled={isBusy}
                onClick={() => fileInputRef.current?.click()}
              >
                Choose File
              </MDButton>
              <Typography variant="body2" color={file ? "text.primary" : "text.secondary"}>
                {file ? file.name : "No file selected"}
              </Typography>
            </MDBox>

            <MDBox>
              <MDButton variant="contained" color="info" disabled={!canPreview} onClick={handlePreview}>
                {phase === "previewing" ? "Reading…" : "Preview File"}
              </MDButton>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Preview only reads the workbook. It writes nothing.
              </Typography>
            </MDBox>
          </Stack>

          {previewError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {previewError}
            </Alert>
          )}
        </Card>

        {preview && (
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Parsed
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {preview.fileName}
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Chip size="small" label={`${preview.parsedRowCount} rows parsed`} />
              <Chip
                size="small"
                color={preview.validRowCount > 0 ? "success" : "default"}
                label={`${preview.validRowCount} valid`}
              />
              <Chip
                size="small"
                color={preview.errorCount > 0 ? "error" : "default"}
                label={`${preview.errorCount} with errors`}
              />
              {/* Most rows legitimately have no contract minimum, so the count of the ones that do
                  is the line Heidi checks against the workbook. */}
              <Chip size="small" label={`${preview.contractMinimumRowCount} with a contract minimum`} />
            </Stack>

            <Table size="small" sx={{ maxWidth: 420 }}>
              <TableBody>
                {totalLines.map((line) => (
                  <TableRow key={line.label}>
                    <TableCell sx={{ borderBottom: "none", pl: 0 }}>
                      <Typography variant="body2" fontWeight={line.emphasis ? "bold" : "regular"}>
                        {line.label}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: "none", pr: 0 }}>
                      <Typography variant="body2" fontWeight={line.emphasis ? "bold" : "regular"}>
                        {formatMoney(line.value)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Totals cover the valid rows only, summed at full precision and shown to 2 decimals.
            </Typography>

            {preview.errors && preview.errors.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  {preview.errors.length} row{preview.errors.length === 1 ? "" : "s"} will not be imported
                </Typography>
                <ErrorRowList errors={preview.errors} />
              </Alert>
            )}
          </Card>
        )}

        {preview && (
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Import
            </Typography>

            {preview.validRowCount > 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Importing writes {preview.validRowCount} row{preview.validRowCount === 1 ? "" : "s"} to the BNSF dispute
                staging table under a new upload batch. Flow cannot undo it — a batch imported twice has to be removed
                in SQL.
              </Alert>
            ) : (
              <Alert severity="error" sx={{ mb: 2 }}>
                No valid rows to import. Fix the workbook and preview it again.
              </Alert>
            )}

            <MDBox>
              <MDButton variant="contained" color="success" disabled={!canImport} onClick={handleImport}>
                {phase === "importing"
                  ? "Importing…"
                  : `Import ${preview.validRowCount} Row${preview.validRowCount === 1 ? "" : "s"}`}
              </MDButton>
              {phase === "imported" && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  This file has been imported. Choose another file to import a different workbook.
                </Typography>
              )}
            </MDBox>

            {importError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  Import failed
                </Typography>
                <Typography variant="body2">{importError}</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Unless the message above says nothing was imported, check whether a batch landed before importing
                  again — a failure after the insert committed looks the same from here, and a second import would
                  duplicate the batch.
                </Typography>
              </Alert>
            )}

            {importResult && (
              <MDBox mt={2}>
                <Alert severity={insertShortfall === 0 ? "success" : "error"}>
                  <Typography variant="body2" fontWeight="bold">
                    {importResult.insertedCount} row{importResult.insertedCount === 1 ? "" : "s"} imported
                    {insertShortfall > 0
                      ? ` — ${insertShortfall} of the ${preview.validRowCount} valid rows did NOT land`
                      : insertShortfall < 0
                      ? ` — ${-insertShortfall} MORE than the ${preview.validRowCount} valid rows previewed`
                      : ""}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {importResult.parsedRowCount} row{importResult.parsedRowCount === 1 ? "" : "s"} re-parsed on the
                    server, {importResult.skippedRowCount} skipped.
                  </Typography>
                  {insertShortfall !== 0 && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      The insert does not match the preview. Do not re-import — check the batch in SQL before anything
                      else, because the missing disputes will expire unpaid.
                    </Typography>
                  )}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Upload batch:</strong> {importResult.uploadBatchId}
                    </Typography>
                    <Tooltip title="Copy upload batch ID">
                      <IconButton size="small" onClick={handleCopyBatchId}>
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Alert>

                {/* The import's OWN error list, not the preview's. They should agree, but the
                    import is the parse that decided what was written, so it is the one shown
                    alongside the result. */}
                {importResult.errors && importResult.errors.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {importResult.errors.length} row{importResult.errors.length === 1 ? "" : "s"} skipped by the
                      import
                    </Typography>
                    <ErrorRowList errors={importResult.errors} />
                  </Alert>
                )}
              </MDBox>
            )}
          </Card>
        )}

        {preview && preview.rows && preview.rows.length > 0 && (
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Rows ({preview.rows.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Paper sx={{ width: "100%", overflow: "hidden" }}>
              <DataGrid
                // Workbook row number is unique within a file and is what the error list names,
                // so it doubles as the grid's row id.
                getRowId={(row) => row.sourceRowNumber}
                sx={{ fontSize: ".95em" }}
                autoHeight
                rows={preview.rows}
                columns={disputeColumns}
                pagination
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                  sorting: { sortModel: [{ field: "sourceRowNumber", sort: "asc" }] },
                }}
                pageSizeOptions={[25, 50, 100]}
                disableColumnMenu
                density="compact"
                getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
              />
            </Paper>
          </Card>
        )}
      </Stack>
    </MDBox>
  );
};

export default BnsfRoadDisputes;
